/**
 * Horizontal band that portaled overlays (dropdowns, action menus, tag popups) must stay inside.
 *
 * The shell caps its content column at `CONTENT_MAX_WIDTH` and keeps the region beside it for side
 * panels/modals; `useShellMetrics` publishes that column's edges as `--app-content-left` /
 * `--app-content-right`. Overlays clamp against those edges instead of the window, so they never
 * spill over the side-panel region.
 *
 * Falls back to the viewport when the variables are absent — layouts that do not use
 * `useShellMetrics` (and tests) then behave exactly as before.
 */
import { Z_LAYERS } from '@styles/zLayers.ts';

export interface ContentBounds {
  left: number;
  right: number;
}

/** Containers that own their own band: an overlay anchored inside one clamps to it, not to the content. */
const PANEL_SELECTOR = '.p-sidebar, .p-dialog, .p-overlaypanel';

const viewportBounds = (): ContentBounds => ({ left: 0, right: document.documentElement.clientWidth });

const readVar = (name: string): number | null => {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name);
  if (!raw) return null;
  const value = parseFloat(raw);
  return Number.isFinite(value) ? value : null;
};

export const getContentBounds = (): ContentBounds => {
  const viewport = viewportBounds();
  const left = readVar('--app-content-left');
  const right = readVar('--app-content-right');
  if (left === null || right === null || right <= left) return viewport;
  return { left: Math.max(0, left), right: Math.min(viewport.right, right) };
};

/**
 * Bounds for an overlay anchored to `anchor`.
 *
 * A trigger that lives in a side panel or dialog belongs to that panel's band — clamping it to the
 * content column would push its popup out from under the very panel it was opened in. Everything
 * else clamps to the content column.
 */
export const getOverlayBounds = (anchor?: Element | null): ContentBounds => {
  const panel = anchor?.closest?.(PANEL_SELECTOR) as HTMLElement | null | undefined;
  if (panel) {
    const rect = panel.getBoundingClientRect();
    if (rect.width > 0) return { left: rect.left, right: rect.right };
  }
  return getContentBounds();
};

/** The elements PrimeReact stacks dynamically; the mask is what carries the z-index. */
const PANEL_MASK_SELECTOR = '.p-sidebar-mask, .p-dialog-mask';

/**
 * z-index for a portaled popup anchored to `anchor`.
 *
 * PrimeReact assigns side panels and dialogs their z-index at open time from a running counter that
 * builds on whatever overlay is open at that moment. While a toast is showing, or a dialog with a
 * large `baseZIndex` is up, a panel opened from there lands far above `--z-popup` — and a popup
 * opened from inside it, portaled to <body> at the fixed layer, is then hidden behind the panel.
 * So a popup anchored inside such a panel sits one step above the panel; everywhere else it stays on
 * the fixed popup layer, so the scale is unchanged for the common case.
 */
export const getOverlayZIndex = (anchor?: Element | null): number => {
  const mask = anchor?.closest?.(PANEL_MASK_SELECTOR);
  if (!mask) return Z_LAYERS.popup;
  const maskZIndex = parseInt(getComputedStyle(mask).zIndex, 10);
  if (!Number.isFinite(maskZIndex)) return Z_LAYERS.popup;
  return Math.max(Z_LAYERS.popup, maskZIndex + 1);
};

/** Vertical placement for an overlay: which side of the anchor it opens on, and how tall it may be. */
export interface OverlayPlacement {
  /** Distance from the top of the viewport, when the overlay opens downwards. */
  top?: number;
  /** Distance from the bottom of the viewport, when it opens upwards. */
  bottom?: number;
  maxHeight: number;
}

/** Below this a scrolling overlay is useless, so it covers part of the anchor instead of shrinking further. */
const MIN_OVERLAY_HEIGHT_PX = 120;

/** An overlay that fills the window reads as a page. */
const MAX_VIEWPORT_RATIO = 0.7;

export interface PlaceOverlayOptions {
  /** Space between the anchor and the overlay. */
  gap: number;
  /** Space kept between the overlay and the window edge. */
  margin: number;
  /** The side a previous call chose. Re-deciding it mid-open moves the overlay under the pointer. */
  openUpwards?: boolean;
}

/**
 * Places an overlay above or below its anchor.
 *
 * The horizontal half of this problem is `getOverlayBounds`; this is the vertical one. Below by
 * default, above when the content does not fit below and there is more room there, and in both cases
 * capped to the space that side actually has — a panel taller than the window scrolls instead of
 * being cut off by it.
 *
 * Returns `null` when the anchor is no longer on screen, which callers should treat as "close me".
 */
export const placeOverlay = (anchor: DOMRect, contentHeight: number, { gap, margin, openUpwards }: PlaceOverlayOptions): OverlayPlacement | null => {
  const viewportHeight = document.documentElement.clientHeight;
  // The anchor has scrolled off screen and taken its row with it. Without this the height clamp below
  // would pin the overlay against the nearest edge, leaving it stranded over content it does not belong to.
  if (anchor.bottom <= 0 || anchor.top >= viewportHeight) return null;
  const cap = viewportHeight * MAX_VIEWPORT_RATIO;
  const spaceBelow = Math.min(viewportHeight - anchor.bottom - gap - margin, cap);
  const spaceAbove = Math.min(anchor.top - gap - margin, cap);

  const upwards = openUpwards ?? (contentHeight > spaceBelow && spaceAbove > spaceBelow);
  const maxHeight = Math.max(MIN_OVERLAY_HEIGHT_PX, upwards ? spaceAbove : spaceBelow);
  // The floor can exceed the space available, so the side is clamped against what is really occupied.
  const height = Math.min(contentHeight || maxHeight, maxHeight);

  return upwards
    ? { bottom: Math.min(viewportHeight - anchor.top + gap, viewportHeight - margin - height), maxHeight }
    : { top: Math.min(anchor.bottom + gap, viewportHeight - margin - height), maxHeight };
};

export default getContentBounds;
